import { PeriodBar } from '@/features/reports/components/PeriodBar';
import { ReportsShell, ReportTabs } from '@/features/reports/components/ReportCard';
import { usePeriodComparison } from '@/features/reports/hooks/useReports';
import { resolveRange, type RangePreset } from '@/features/reports/lib/range';
import { DayEndSection } from '@/features/reports/sections/DayEndSection';
import { OperationsSection } from '@/features/reports/sections/OperationsSection';
import { PaymentsSection } from '@/features/reports/sections/PaymentsSection';
import { SalesSection } from '@/features/reports/sections/SalesSection';
import { TopSellersSection } from '@/features/reports/sections/TopSellersSection';
import { featureFlags } from '@/featureFlags';
import { useI18nStore, useT } from '@/i18n';
import { useRestaurantTimezone } from '@/stores/restaurant.store';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import type { ComparisonBasis } from '@restaurant/api-client';
import { Navbar, Page, Subnavbar } from 'framework7-react';
import { useMemo, useState } from 'react';

type Segment = 'SALES' | 'OPERATIONS' | 'TOP_SELLERS' | 'PAYMENTS' | 'DAY_END';

// One reports page. The segmented control in the navbar switches between the
// report domains; there is no separate hub list and no detail routes, so a
// report is always one tap away rather than two.
function ReportsPage() {
  const t = useT();
  const locale = useI18nStore((s) => s.locale);
  const tz = useRestaurantTimezone();
  const [preset, setPreset] = useState<RangePreset>('LAST_7');
  const [basis, setBasis] = useState<ComparisonBasis>('PREVIOUS_PERIOD');
  const range = useMemo(() => resolveRange(preset, tz), [preset, tz]);

  // On mobile Reports is pushed from the Settings list, so it needs a way back.
  // On desktop it is a sidebar destination and the back link would be a dead
  // chevron, which is what the hook returns `undefined` for.
  const backLink = useSettingsDetailBackLink() ? t.back : undefined;

  // Dine-in (table ordering) is not released; the Operations segment is
  // entirely dine-in, so it is hidden with the same flag.
  const dineInEnabled = featureFlags.tableOrdering;
  const [rawSegment, setSegment] = useState<Segment>('SALES');
  const segment: Segment = !dineInEnabled && rawSegment === 'OPERATIONS' ? 'SALES' : rawSegment;

  // Only used to label the comparison side of the period bar with the dates the
  // server actually compared against.
  const { data: comparison } = usePeriodComparison(range, basis);

  const ov = t.reports.overview;
  const segments: { key: Segment; label: string }[] = [
    { key: 'SALES', label: ov.tabSales },
    ...(dineInEnabled ? [{ key: 'OPERATIONS' as const, label: ov.tabOperations }] : []),
    { key: 'TOP_SELLERS', label: t.reports.topItems.title },
    { key: 'PAYMENTS', label: t.settingsFeature.paymentReconciliation },
    { key: 'DAY_END', label: t.settingsFeature.dayEnd },
  ];

  return (
    <Page name="reports">
      <Navbar title={t.nav.reports} backLink={backLink}>
        <Subnavbar inner={false}>
          <div className="rpt-subnav">
            <ReportTabs tabs={segments} value={segment} onChange={setSegment} />
          </div>
        </Subnavbar>
      </Navbar>

      <ReportsShell>
        {/* Day-end closes out a single day and carries its own picker, so the
            period bar would be meaningless there. */}
        {segment !== 'DAY_END' && (
          <PeriodBar
            preset={preset}
            onPresetChange={setPreset}
            basis={basis}
            onBasisChange={setBasis}
            currentRange={range}
            comparisonRange={comparison?.previousRange ?? null}
            locale={locale}
          />
        )}

        {segment === 'SALES' && (
          <SalesSection range={range} basis={basis} locale={locale} dineInEnabled={dineInEnabled} />
        )}
        {segment === 'OPERATIONS' && <OperationsSection range={range} />}
        {segment === 'TOP_SELLERS' && <TopSellersSection range={range} />}
        {segment === 'PAYMENTS' && <PaymentsSection range={range} />}
        {segment === 'DAY_END' && <DayEndSection />}
      </ReportsShell>
    </Page>
  );
}

export default ReportsPage;
